import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dashboardMetricApi from "../api/dashboardMetricApi";
import userSessionApi from "../api/userSessionApi";
import authApi from "../api/authApi";
import { tokenStore } from "../api/axiosClient";
import RequirePermission from "../auth/RequirePermission";

export default function DashboardPage() {
    const [metrics, setMetrics] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [topPages, setTopPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [signingOut, setSigningOut] = useState(false);
    const [error, setError] = useState("");
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [user, setUser] = useState(() => {
        try {
            const stores = [localStorage, sessionStorage].filter(Boolean);
            for (const store of stores) {
                const raw = store.getItem("user");
                if (raw) return JSON.parse(raw);
            }
        } catch {
            return null;
        }
        return null;
    });
    const [adminForm, setAdminForm] = useState({
        email: "",
        fullName: "",
        password: "",
    });
    const [toast, setToast] = useState(null);

    const navigate = useNavigate();
    const isSuperAdmin = (user?.role || "").toLowerCase() === "superadmin";

    const showToast = (msg, tone = "info") => {
        setToast({ msg, tone });
        setTimeout(() => setToast(null), 3000);
    };

    const extractList = (data, keys = []) => {
        if (Array.isArray(data)) return data;

        for (const key of keys) {
            if (Array.isArray(data?.[key])) return data[key];
        }

        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data?.data)) return data.data;

        return [];
    };

    const normalizeMetrics = (data) => {
        const source = data?.metrics || data?.data || data || {};

        return {
            totalSessions:
                source.totalSessions ??
                source.sessions ??
                source.sessionCount ??
                source.totalSessionCount ??
                0,
            activeSessions:
                source.activeSessions ??
                source.activeSessionCount ??
                source.currentActiveSessions ??
                0,
            totalEvents:
                source.totalEvents ??
                source.events ??
                source.eventCount ??
                source.totalEventCount ??
                0,
            totalPageviews:
                source.totalPageviews ??
                source.pageviews ??
                source.totalViews ??
                source.pageViewCount ??
                0,
        };
    };

    const fetchData = async () => {
        setLoading(true);
        setError("");

        try {
            const [metricsRes, sessionsRes, topPagesRes] = await Promise.all([
                dashboardMetricApi.getMetrics(),
                userSessionApi.getRecentSessions({ page: 1, pageSize: 10 }),
                dashboardMetricApi.getTopPages(),
            ]);

            setMetrics(normalizeMetrics(metricsRes.data));
            setSessions(extractList(sessionsRes.data, ["sessions", "recentSessions"]));
            setTopPages(extractList(topPagesRes.data, ["topPages", "pages"]));
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err.message ||
                "Failed to load dashboard data";
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    const fetchUser = async () => {
        try {
            const { data } = await authApi.me();
            setUser(data);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401) {
                tokenStore.clear();
                navigate("/login", { replace: true });
                return;
            }

            const message =
                err?.response?.data?.message ||
                err.message ||
                "Failed to load profile";
            setError(message);
        }
    };

    useEffect(() => {
        fetchData();
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdminSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!isSuperAdmin) {
            setError("Only SuperAdmin can create admins");
            return;
        }

        try {
            await authApi.createAdmin({
                email: adminForm.email,
                fullName: adminForm.fullName,
                password: adminForm.password,
            });

            showToast("Admin created");
            setAdminForm({ email: "", fullName: "", password: "" });
            setShowAdminModal(false);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err.message ||
                "Failed to create admin";
            setError(message);
        }
    };

    const handleLogout = async () => {
        setError("");
        setSigningOut(true);
        const refreshToken = tokenStore.getRefreshToken();

        try {
            if (refreshToken) {
                await authApi.logout({ refreshToken, allDevices: true });
            }
        } catch {
            // server-side logout is best-effort; proceed regardless
        } finally {
            tokenStore.clear();
            navigate("/login", { replace: true });
            setSigningOut(false);
        }
    };

    const fmt = (val) => {
        if (val === null || val === undefined || val === "" || val === 0) return "---";
        const dateValue = typeof val === "number" ? val : Number(val) || val;
        const date = new Date(dateValue);
        return Number.isNaN(date.getTime()) ? "---" : date.toLocaleString();
    };

    const statCards = [
        {
            key: "totalSessions",
            label: "Total Sessions",
            value: metrics?.totalSessions ?? 0,
        },
        {
            key: "activeSessions",
            label: "Active Sessions",
            value: metrics?.activeSessions ?? 0,
        },
        {
            key: "totalEvents",
            label: "Total Events",
            value: metrics?.totalEvents ?? 0,
        },
        {
            key: "totalPageviews",
            label: "Total Pageviews",
            value: metrics?.totalPageviews ?? 0,
        },
    ];

    return (
        <div className="shell">
            <header className="hero">
                <div>
                    <p className="eyebrow">Analytics Dashboard</p>
                    <h1>Traffic & Engagement Overview</h1>
                    <p className="lede">
                        Live analytics powered by your real dashboard APIs. No demo drama, no fake rows.
                    </p>
                </div>

                <div className="hero-right">
                    {user && (
                        <div className="user-chip" title={user.email}>
                            <div className="user-avatar">
                                {user.fullName?.charAt(0) || user.email?.charAt(0) || "A"}
                            </div>
                            <div className="user-meta">
                                <span className="user-name">{user.fullName || "Authenticated"}</span>
                                <span className="user-role">{user.role || "User"}</span>
                            </div>
                        </div>
                    )}

                    <div className="actions">
                        <RequirePermission code="ivt.read">
                            <button className="ghost" type="button" onClick={() => navigate("/modules")}>
                                All modules
                            </button>
                        </RequirePermission>
                        <RequirePermission code="workspace.read">
                            <button className="ghost" type="button" onClick={() => navigate("/notifications")}>
                                Notifications
                            </button>
                            <button className="ghost" type="button" onClick={() => navigate("/account")}>
                                Account
                            </button>
                        </RequirePermission>
                        <RequirePermission code="ivt.read">
                            <button
                                className="primary"
                                type="button"
                                onClick={() => navigate("/security")}
                            >
                                Security Center
                            </button>
                        </RequirePermission>

                        <RequirePermission code="analytics.read">
                            <button
                                className="ghost"
                                type="button"
                                onClick={() => navigate("/analytics")}
                            >
                                Analytics
                            </button>
                        </RequirePermission>

                        <RequirePermission code="users.read">
                            <button
                                className="ghost"
                                type="button"
                                onClick={() => navigate("/users")}
                            >
                                Users
                            </button>
                        </RequirePermission>

                        <RequirePermission code="roles.read">
                            <button
                                className="ghost"
                                type="button"
                                onClick={() => navigate("/roles")}
                            >
                                Roles
                            </button>
                        </RequirePermission>

                        <RequirePermission code="sites.read">
                            <button
                                className="ghost"
                                type="button"
                                onClick={() => navigate("/sites")}
                            >
                                Sites
                            </button>
                        </RequirePermission>

                        {isSuperAdmin && (
                            <button
                                className="primary"
                                type="button"
                                onClick={() => setShowAdminModal(true)}
                            >
                                New Admin
                            </button>
                        )}

                        <button
                            className="ghost"
                            onClick={fetchData}
                            disabled={loading || signingOut}
                        >
                            {loading ? "Refreshing..." : "Refresh"}
                        </button>

                        <button
                            className="ghost danger"
                            onClick={handleLogout}
                            disabled={signingOut || loading}
                        >
                            {signingOut ? "Signing out..." : "Logout"}
                        </button>
                    </div>
                </div>
            </header>

            {toast && <div className={`toast ${toast.tone}`}>{toast.msg}</div>}
            {error && <div className="toast error">{error}</div>}

            <section className="stats-grid">
                {statCards.map((card) => (
                    <article className="card stat-card" key={card.key}>
                        <p className="eyebrow">{card.label}</p>
                        <h2>{loading ? "..." : Number(card.value || 0).toLocaleString()}</h2>
                        <p className="hint">Live dashboard data</p>
                    </article>
                ))}
            </section>

            <section className="dashboard-grid">
                <section className="table-wrap">
                    <div className="table-head">
                        <div>
                            <p className="eyebrow">Recent Sessions</p>
                            <h3>Latest tracked sessions</h3>
                            <p className="hint">
                                Times shown in your local time. Status comes directly from dashboard data.
                            </p>
                        </div>
                    </div>

                    <div className="table sessions">
                        <div className="row head">
                            <span>Session</span>
                            <span>Site</span>
                            <span>Status</span>
                            <span>Start</span>
                            <span>Exit</span>
                            <span>IP</span>
                        </div>

                        {sessions.map((s, index) => (
                            <div className="row" key={s.id || s.sessionId || index}>
                                <span title={s.sessionId || s.id || ""}>
                                    {String(s.sessionId || s.id || "---").slice(0, 16)}
                                </span>
                                <span title={s.siteId || ""}>
                                    {s.siteId || "---"}
                                </span>
                                <span>
                                    <span
                                        className={`badge ${
                                            (s.status || "").toLowerCase() === "active" ? "success" : "neutral"
                                        }`}
                                    >
                                        {s.status || "Unknown"}
                                    </span>
                                </span>
                                <span>{fmt(s.startTime)}</span>
                                <span>{fmt(s.exitTime)}</span>
                                <span title={s.ipAddress || ""}>
                                    {s.ipAddress || "---"}
                                </span>
                            </div>
                        ))}

                        {!loading && !sessions.length && (
                            <div className="row empty">No recent sessions found</div>
                        )}
                    </div>
                </section>

                <section className="table-wrap">
                    <div className="table-head">
                        <div>
                            <p className="eyebrow">Top Pages</p>
                            <h3>Most visited pages</h3>
                            <p className="hint">Based on tracked live analytics data.</p>
                        </div>
                    </div>

                    <div className="table pages">
                        <div className="row head">
                            <span>Page</span>
                            <span>Views</span>
                            <span>Sessions</span>
                        </div>

                        {topPages.map((page, index) => (
                            <div className="row" key={page.pageUrl || page.url || page.path || index}>
                                <span title={page.pageUrl || page.url || page.path || ""}>
                                    {page.pageUrl || page.url || page.path || "---"}
                                </span>
                                <span>
                                    {Number(
                                        page.views ??
                                        page.pageviews ??
                                        page.totalViews ??
                                        page.count ??
                                        0
                                    ).toLocaleString()}
                                </span>
                                <span>
                                    {Number(
                                        page.sessions ??
                                        page.sessionCount ??
                                        page.uniqueSessions ??
                                        0
                                    ).toLocaleString()}
                                </span>
                            </div>
                        ))}

                        {!loading && !topPages.length && (
                            <div className="row empty">No top pages found</div>
                        )}
                    </div>
                </section>
            </section>

            {showAdminModal && (
                <div className="modal-backdrop" onClick={() => setShowAdminModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-head">
                            <div>
                                <p className="eyebrow">Create Admin</p>
                                <h3>Provision console access</h3>
                                <p className="hint">Share credentials securely after creation.</p>
                            </div>
                            <button
                                className="ghost"
                                type="button"
                                onClick={() => setShowAdminModal(false)}
                            >
                                Close
                            </button>
                        </div>

                        <form className="stack" onSubmit={handleAdminSubmit}>
                            <label>
                                Work email
                                <input
                                    name="email"
                                    type="email"
                                    value={adminForm.email}
                                    onChange={(e) =>
                                        setAdminForm({ ...adminForm, email: e.target.value })
                                    }
                                    required
                                    placeholder="admin@company.com"
                                />
                            </label>

                            <label>
                                Full name
                                <input
                                    name="fullName"
                                    value={adminForm.fullName}
                                    onChange={(e) =>
                                        setAdminForm({ ...adminForm, fullName: e.target.value })
                                    }
                                    required
                                    placeholder="Security Admin"
                                />
                            </label>

                            <label>
                                Password
                                <input
                                    name="password"
                                    type="password"
                                    value={adminForm.password}
                                    onChange={(e) =>
                                        setAdminForm({ ...adminForm, password: e.target.value })
                                    }
                                    required
                                    placeholder="Strong password"
                                />
                            </label>

                            <button type="submit" className="primary">
                                Create Admin
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}